import { format, isValid } from 'date-fns';

import { TierName, TierStatusName } from '@order/graphql';

import type { AccountSweetpassCancellationSection } from '../../useSweetpassAccountContentfulData';
import type {
  AsyncCta,
  GetSectionProps,
  SweetpassSectionProps,
} from '../types';

export const getCancellationSection = (
  props: GetCancellationSectionProps,
): SweetpassCancellationSectionProps | undefined => {
  const {
    name,
    tier,
    tierStatus,
    activeSubscription,
    cancellationDataTitle,
    cancellationDataBody,
    cancellationDataCancellationCta,
    cancelSubscriptionHandler,
    formatMessage,
  } = props;

  if (
    !activeSubscription ||
    tier?.name !== TierName.SweetpassPlus ||
    tierStatus === TierStatusName.CancellationPending
  ) {
    return undefined;
  }

  const { nextBillingDate } = activeSubscription;

  const formattedNextBillingDate = isValid(new Date(nextBillingDate))
    ? format(new Date(nextBillingDate), 'MM/dd/yyyy')
    : null;

  if (!formattedNextBillingDate) return undefined;

  return {
    name,
    title: cancellationDataTitle,

    bodyText: (cancellationDataBody as string).replace(
      '{nextBillingDate}',
      formattedNextBillingDate,
    ),

    cta: {
      palette: 'caution',
      title: cancellationDataCancellationCta,
      accessibilityLabel: formatMessage(
        'account.sweetpass-membership.cancellation.cta.accessibility-label',
      ),
      accessibilityHint: formatMessage(
        'account.sweetpass-membership.cancellation.cta.accessibility-hint',
      ),
      handler: cancelSubscriptionHandler,
    },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GetCancellationSectionProps = {
  readonly cancelSubscriptionHandler: () => Promise<void>;
} & AccountSweetpassCancellationSection &
  GetSectionProps;

type SweetpassCancellationCta = AsyncCta;

export type SweetpassCancellationSectionProps = {
  readonly name: string;
  readonly title: string;
  readonly bodyText: string;
  readonly cta: SweetpassCancellationCta;
} & SweetpassSectionProps;
